import {
    ArrowLeftIcon,
    ArrowsRightLeftIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

import { MOCK_CONFLICTS, MOCK_PROVIDERS, MockConflict, PROVIDER_TYPE_LABEL } from './mockData'

const STATUS_STYLES: Record<MockConflict['status'], { label: string; cls: string }> = {
    open: { label: 'Open', cls: 'bg-red-100 text-red-800' },
    clarifying: { label: 'Clarifying', cls: 'bg-amber-100 text-amber-800' },
    resolved: { label: 'Resolved', cls: 'bg-green-100 text-green-800' },
}

export default function Conflicts() {
    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to='/admin/family-care-team-preview/team'
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to care team
            </Link>

            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Conflicts</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    When two providers disagree, BrightPath stops the roadmap from updating silently and asks
                    you for input.
                </p>
            </div>

            <ul className='space-y-3'>
                {MOCK_CONFLICTS.map((c) => (
                    <ConflictRow key={c.id} conflict={c} />
                ))}
            </ul>
        </div>
    )
}

function ConflictRow({ conflict }: { conflict: MockConflict }) {
    const provA = MOCK_PROVIDERS.find((p) => p.id === conflict.providerAId)
    const provB = MOCK_PROVIDERS.find((p) => p.id === conflict.providerBId)
    const isResolved = conflict.status === 'resolved'
    const status = STATUS_STYLES[conflict.status]
    return (
        <li
            className={`bg-white border rounded-xl ${
                isResolved ? 'border-neutral-200' : 'border-red-200'
            }`}
        >
            <Link
                to={`/admin/family-care-team-preview/conflicts/${conflict.id}`}
                className='flex items-start gap-3 p-4 hover:bg-neutral-50/50 rounded-xl'
            >
                <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isResolved ? 'bg-green-50' : 'bg-red-50'
                    }`}
                >
                    {isResolved ? (
                        <CheckCircleIcon className='w-5 h-5 text-green-600' />
                    ) : (
                        <ExclamationCircleIcon className='w-5 h-5 text-red-600' />
                    )}
                </div>
                <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-center gap-2 mb-1'>
                        <span className='text-sm font-semibold text-neutral-900'>{conflict.title}</span>
                        <span
                            className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${status.cls}`}
                        >
                            {status.label}
                        </span>
                    </div>
                    <p className='text-xs text-neutral-700'>{conflict.summary}</p>
                    {provA && provB && (
                        <div className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-500'>
                            <span className='inline-flex items-center gap-1'>
                                <ArrowsRightLeftIcon className='w-3 h-3' />
                                {provA.clinicianName} ({PROVIDER_TYPE_LABEL[provA.type]})
                                <span className='mx-1 text-neutral-400'>vs.</span>
                                {provB.clinicianName} ({PROVIDER_TYPE_LABEL[provB.type]})
                            </span>
                            <span className='text-neutral-400'>·</span>
                            <span>detected {conflict.detectedAt}</span>
                        </div>
                    )}
                </div>
                <ChevronRightIcon className='w-4 h-4 text-neutral-400 flex-shrink-0 mt-1' />
            </Link>
        </li>
    )
}
